import { useEffect, useState, useCallback } from "react";
import { Link, useSearchParams } from "react-router-dom";
import api, { apiError } from "@/lib/api";
import { DAYS_PT, mondayOf, addDays, dayIndex, fmtDM, ItemPreview, MK_STATUS, downloadBlob, captionOf, copyText } from "@/lib/marketing";
import { ItemEditor } from "@/components/MarketingItemEditor";
import { PhotoLibraryDialog } from "@/components/MarketingPhotos";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { CaretLeft, CaretRight, DownloadSimple, Copy, PencilSimple, FileZip, Images, Palette, CircleNotch, Plus, Trash, CalendarBlank, Sparkle, ClockCounterClockwise, Gear } from "@phosphor-icons/react";
import { toast } from "sonner";

function ItemCard({ it, version, onEdit, onDelete, onRegen, regenId }) {
  const st = MK_STATUS[it.status] || MK_STATUS.rascunho;
  const copy = async () => {
    const c = captionOf(it);
    if (!c) return toast.error("Esta legenda está vazia");
    if (await copyText(c)) toast.success("Legenda copiada");
    else toast.error("Não foi possível copiar. Abra o item e copie o texto manualmente.");
  };
  return (
    <div className="stagger-item flex flex-col rounded-xl border border-border bg-card p-3" data-testid={`mk-item-${it.id}`}>
      <div className="mb-2 flex items-center justify-between gap-2">
        <span className="text-xs font-semibold">{DAYS_PT[dayIndex(it.day)]} {fmtDM(it.day)}</span>
        <Badge variant="outline" className={`${st.cls} text-[10px]`} data-testid={`mk-item-status-${it.id}`}>{st.label}</Badge>
      </div>
      <button type="button" onClick={() => onEdit(it)} className="block"><ItemPreview item={it} version={version} /></button>
      <p className="mt-2 line-clamp-2 min-h-[2.5rem] text-xs text-muted-foreground">{it.text || "Sem legenda — clique em Editar."}</p>
      <div className="mt-2 grid grid-cols-5 gap-1">
        <Button size="icon" variant="outline" className="h-8 w-full" onClick={() => onRegen(it)} disabled={!!regenId} title="Gerar de novo (IA)" data-testid={`mk-regen-${it.id}`}>
          {regenId === it.id ? <CircleNotch size={15} className="animate-spin" /> : <Sparkle size={15} />}
        </Button>
        <Button size="icon" variant="outline" className="h-8 w-full" onClick={() => onEdit(it)} title="Editar" data-testid={`mk-edit-${it.id}`}><PencilSimple size={15} /></Button>
        <Button size="icon" variant="outline" className="h-8 w-full" title="Descarregar imagem" data-testid={`mk-download-${it.id}`}
          onClick={() => downloadBlob(`/marketing/items/${it.id}/image.png`, `${it.day}_${it.type}.png`)}><DownloadSimple size={15} /></Button>
        <Button size="icon" variant="outline" className="h-8 w-full" onClick={copy} title="Copiar legenda" data-testid={`mk-copy-${it.id}`}><Copy size={15} /></Button>
        <Button size="icon" variant="outline" className="h-8 w-full text-destructive" onClick={() => onDelete(it)} title="Eliminar" data-testid={`mk-delete-${it.id}`}><Trash size={15} /></Button>
      </div>
    </div>
  );
}

function Section({ title, size, items, version, onEdit, onDelete, onAdd, onRegen, regenId, testid, cols }) {
  return (
    <section className="space-y-3" data-testid={testid}>
      <div className="flex items-center justify-between">
        <h2 className="font-display text-lg font-bold">{title} <span className="text-sm font-normal text-muted-foreground">· {items.length} · {size}</span></h2>
        <Button variant="ghost" size="sm" onClick={onAdd} data-testid={`${testid}-add`}><Plus size={15} className="mr-1" /> Adicionar</Button>
      </div>
      <div className={`grid gap-3 ${cols}`}>
        {items.map((it) => <ItemCard key={it.id} it={it} version={version} onEdit={onEdit} onDelete={onDelete} onRegen={onRegen} regenId={regenId} />)}
      </div>
    </section>
  );
}

export default function Marketing() {
  const [params, setParams] = useSearchParams();
  const monday = params.get("week") ? mondayOf(new Date(`${params.get("week")}T12:00:00`)) : mondayOf();
  const setMonday = (m) => setParams(m === mondayOf() ? {} : { week: m });
  const [week, setWeek] = useState(null);
  const [regenId, setRegenId] = useState(null);
  const [editing, setEditing] = useState(null);
  const [library, setLibrary] = useState(false);
  const [zipBusy, setZipBusy] = useState(false);
  const [version, setVersion] = useState(0);

  const load = useCallback(async (silent = false) => {
    if (!silent) setWeek(null);
    try { setWeek((await api.get(`/marketing/weeks/${monday}`)).data); } catch (e) { toast.error(apiError(e)); }
  }, [monday]);
  useEffect(() => { load(); }, [load]);
  const running = week?.generation?.status === "running";
  useEffect(() => {
    if (!running) return undefined;
    const t = setInterval(() => load(true), 4000);
    return () => clearInterval(t);
  }, [running, load]);

  const generate = async () => {
    if (!window.confirm("Gerar com IA os textos de todos os itens em Rascunho desta semana? Os textos em Rascunho serão reescritos.")) return;
    try {
      await api.post(`/marketing/weeks/${monday}/generate`);
      setWeek((w) => ({ ...w, generation: { status: "running" } }));
    } catch (e) { toast.error(apiError(e)); }
  };
  const regen = async (it) => {
    setRegenId(it.id);
    try {
      const { data } = await api.post(`/marketing/items/${it.id}/regenerate`);
      replace(data);
      toast.success("Nova versão gerada");
    } catch (e) { toast.error(apiError(e)); } finally { setRegenId(null); }
  };

  const replace = (it) => setWeek((w) => ({ ...w, items: w.items.map((x) => (x.id === it.id ? it : x)).sort((a, b) => a.day.localeCompare(b.day)) }));
  const add = async (type) => {
    try {
      const { data } = await api.post(`/marketing/weeks/${monday}/items`, { type, day: monday, template: "photo", status: "rascunho" });
      setWeek((w) => ({ ...w, items: [...w.items, data] }));
      setEditing(data);
    } catch (e) { toast.error(apiError(e)); }
  };
  const del = async (it) => {
    if (!window.confirm("Eliminar este item?")) return;
    try { await api.delete(`/marketing/items/${it.id}`); setWeek((w) => ({ ...w, items: w.items.filter((x) => x.id !== it.id) })); } catch (e) { toast.error(apiError(e)); }
  };
  const zip = async () => { setZipBusy(true); await downloadBlob(`/marketing/weeks/${monday}/download.zip`, `marketing_${monday}.zip`); setZipBusy(false); };
  const isCurrent = monday === mondayOf();
  const posts = week?.items.filter((i) => i.type === "post") || [];
  const stories = week?.items.filter((i) => i.type === "story") || [];

  return (
    <div className="space-y-6" data-testid="marketing-page">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-muted-foreground">Marketing</p>
          <h1 className="font-display text-3xl font-extrabold tracking-tight">Planeamento semanal</h1>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" className="rounded-full" asChild data-testid="mk-history-link"><Link to="/marketing/historico"><ClockCounterClockwise size={16} className="mr-1.5" /> Histórico</Link></Button>
          <Button variant="outline" className="rounded-full" asChild data-testid="mk-settings-link"><Link to="/marketing/configuracoes"><Gear size={16} className="mr-1.5" /> Configurações</Link></Button>
          <Button variant="outline" className="rounded-full" asChild data-testid="mk-brand-link"><Link to="/marketing/marca"><Palette size={16} className="mr-1.5" /> Ficha de marca</Link></Button>
          <Button variant="outline" className="rounded-full" onClick={() => setLibrary(true)} data-testid="mk-open-library"><Images size={16} className="mr-1.5" /> Carregar fotos da obra</Button>
          <Button className="rounded-full" onClick={zip} disabled={zipBusy || !week?.items.length} data-testid="mk-download-week">
            {zipBusy ? <CircleNotch className="mr-1.5 animate-spin" /> : <FileZip size={16} className="mr-1.5" />} Descarregar a semana toda
          </Button>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-2 rounded-xl border border-border bg-card p-2">
        <Button variant="ghost" size="icon" onClick={() => setMonday(addDays(monday, -7))} data-testid="mk-prev-week"><CaretLeft size={18} /></Button>
        <div className="flex-1 text-center">
          <div className="font-display font-bold" data-testid="mk-week-label">Semana de {fmtDM(monday)} a {fmtDM(addDays(monday, 6))}/{addDays(monday, 6).slice(0, 4)}</div>
          {isCurrent && <div className="text-[11px] font-semibold uppercase tracking-wider text-primary">Semana atual</div>}
        </div>
        {!isCurrent && <Button variant="ghost" size="sm" onClick={() => setMonday(mondayOf())} data-testid="mk-this-week"><CalendarBlank size={15} className="mr-1" /> Hoje</Button>}
        <Button variant="ghost" size="icon" onClick={() => setMonday(addDays(monday, 7))} data-testid="mk-next-week"><CaretRight size={18} /></Button>
      </div>

      <div className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-primary/30 bg-accent px-4 py-3" data-testid="mk-ai-bar">
        <div className="text-sm text-accent-foreground">
          {running ? <span className="flex items-center gap-2" data-testid="mk-ai-running"><CircleNotch className="animate-spin" /> A IA está a escrever os textos da semana…</span>
            : week?.generation?.status === "error" ? <span className="text-destructive" data-testid="mk-ai-error">A última geração falhou. Tente novamente.</span>
            : week?.generation?.status === "done" ? <span data-testid="mk-ai-done">Textos gerados {week.generation.source === "auto" ? "automaticamente (segunda 7h)" : "com IA"}. Reveja e aprove cada item.</span>
            : <span>Geração automática todas as segundas às 7h (hora suíça). Pode também gerar agora.</span>}
        </div>
        <Button className="rounded-full" onClick={generate} disabled={running || !week} data-testid="mk-generate-week">
          <Sparkle size={16} className="mr-1.5" /> Gerar semana com IA
        </Button>
      </div>

      {!week ? <div className="flex h-60 items-center justify-center"><CircleNotch size={26} className="animate-spin text-primary" /></div> : (
        <>
          <Section title="Publicações (feed)" size="1080×1350" items={posts} version={version} onEdit={setEditing} onDelete={del}
            onAdd={() => add("post")} onRegen={regen} regenId={regenId} testid="mk-posts" cols="grid-cols-2 md:grid-cols-3 xl:grid-cols-4" />
          <Section title="Stories" size="1080×1920" items={stories} version={version} onEdit={setEditing} onDelete={del}
            onAdd={() => add("story")} onRegen={regen} regenId={regenId} testid="mk-stories" cols="grid-cols-2 sm:grid-cols-3 lg:grid-cols-5" />
        </>
      )}

      <ItemEditor item={editing} monday={monday} brandVersion={version} onClose={() => setEditing(null)}
        onSaved={(it, keepOpen) => { replace(it); setEditing(keepOpen ? it : null); }} />
      <PhotoLibraryDialog open={library} onClose={() => setLibrary(false)} onChanged={() => setVersion((v) => v + 1)} />
    </div>
  );
}
